<template>
  <div class="flip-timer">
    <div v-for="(unit, index) in countdown" :key="index" class="time-unit">
      <div class="flip">
        <div class="card">
          <div class="face">{{ unit.value }}</div>
        </div>
      </div>
      <span class="unit-label">{{ unit.label }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';

const props = defineProps({
  endDate: {
    type: [Date, String], // Accept both Date and String
    default: () => new Date(new Date().getTime() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
  },
});

const countdown = ref([
  { label: 'DAYS', value: '00' },
  { label: 'HOURS', value: '00' },
  { label: 'MINUTES', value: '00' },
  { label: 'SECONDS', value: '00' },
]);

const updateCountdown = () => {
  let endDateObj = props.endDate;

  // If endDate is a string, parse it to a Date object
  if (typeof props.endDate === 'string') {
    endDateObj = new Date(props.endDate);
  }

  if (!(endDateObj instanceof Date)) {
    console.warn(`Invalid endDate prop passed to CountDownTimer. It should be a Date object or a valid ISO date string. endDate is: ${props.endDate}`);
    return;
  }

  const now = new Date().getTime();
  const distance = endDateObj.getTime() - now;

  const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((distance % (1000 * 60)) / 1000);

  countdown.value = [
    { label: 'DAYS', value: String(days).padStart(2, '0') },
    { label: 'HOURS', value: String(hours).padStart(2, '0') },
    { label: 'MINUTES', value: String(minutes).padStart(2, '0') },
    { label: 'SECONDS', value: String(seconds).padStart(2, '0') },
  ];
};

let interval;

onMounted(() => {
  updateCountdown();
  interval = setInterval(updateCountdown, 1000);
});

watch(
  () => props.endDate,
  (newDateVal) => {
    let newDateObj = newDateVal;

    if (typeof newDateVal === 'string') {
      newDateObj = new Date(newDateVal);
    }

    if (newDateObj instanceof Date) {
      clearInterval(interval);
      updateCountdown();
      interval = setInterval(updateCountdown, 1000);
    }
  }
);

</script>



<style lang="scss">
@import '~/assets/constants.scss';

.flip-timer {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .time-unit {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 0.625rem;  // 10px to rem

    .card {
      width: 3.75rem;    // 60px to rem
      height: 3.75rem;  // 60px to rem
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 2.0rem;
      font-weight: bold;
      color: $light-white;
      text-shadow: 0.125rem 0.125rem 0.1875rem rgba($black, 0.3);  // px to rem

      background: linear-gradient(to bottom, $light-black, $header-gray);
      border-radius: 0.5rem;  // 8px to rem
      box-shadow: 
        0 0.25rem 0.5rem rgba($black, 0.2), 
        0 0.375rem 1.25rem rgba($black, 0.19),
        inset 0 0 1.25rem rgba($light-white, 0.2),
        inset 0 0 1.25rem rgba($black, 0.2);
    }

    .unit-label {
      margin-top: 0.625rem;  // 10px to rem
      font-size: 0.75rem;   // 12px to rem
      text-transform: uppercase;
      display: flex;
      justify-content: center;
      align-items: center;
      color: $light-white;
    }
  }
}



</style>
